import clsx from 'clsx'
import { useCallback, useContext } from 'react'
import { FieldErrors, UseFormRegister } from 'react-hook-form'
import isEmail from 'validator/es/lib/isEmail'
import { Context } from 'src/Subscribe_'
import type { FieldValues } from './RecipientsInformation'

type CheckBody = {
  email: string
  nonce: string | undefined
}

type CheckResult = {
  has_started_renewal: boolean
  subscriber_already_exists: boolean
}
const RecipientsFullNameAndEmailAddress = ({
  errors,
  otherEmails,
  register,
}: {
  errors: FieldErrors<FieldValues>
  otherEmails: string[]
  register: UseFormRegister<FieldValues>
}) => {
  const { state } = useContext(Context)

  const check = useCallback(
    async (email: string): Promise<CheckResult> => {
      let body: CheckBody = {
        email,
        nonce: state.nonce,
      }
      try {
        const response = await fetch('/subscribe/check', {
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        })
        if (response.ok) {
          const result = await response.json()
          return result
        }
      } catch (e) {
        console.error(`Encountered error when checking email address: ${e}`)
      }
      return {
        has_started_renewal: false,
        subscriber_already_exists: false,
      }
    },
    [state.nonce]
  )

  return (
    <div>
      <fieldset className="form-item">
        <legend>
          <span>Recipient’s full name and email address*</span>
        </legend>
        <div>
          <div className="form-item">
            <label htmlFor="edit-name">Full name</label>
            <input
              className={clsx('form-text', errors.name && 'error')}
              id="edit-name"
              placeholder=" "
              size={60}
              {...register('name', {
                validate: (value) => {
                  if (!value) {
                    return 'Field is required'
                  }
                  if (value.length > 35) {
                    return 'Field is limited to 35 characters'
                  }
                  return undefined
                },
              })}
            />
            {errors.name && (
              <div className="form-item--error-message">
                {errors.name.message}
              </div>
            )}
          </div>
          <div className="js-form-item form-item js-form-type-email form-item-email js-form-item-email">
            <label htmlFor="edit-email">Email address</label>
            <input
              className={clsx('form-email', errors.email && 'error')}
              id="edit-email"
              placeholder=" "
              size={60}
              {...register('email', {
                validate: async (value) => {
                  if (!value) {
                    return 'Field is required'
                  }
                  if (value.length > 50) {
                    return 'Field is limited to 50 characters'
                  }
                  if (!isEmail(value)) {
                    return 'Valid email required'
                  }
                  if (otherEmails.includes(value)) {
                    return 'Email address is already a recipient'
                  }
                  const result = await check(value)
                  if (result.has_started_renewal) {
                    return 'Recipient has a pending renewal. Please email us at <a href="mailto:nureads@jewishbooks.org">nureads@jewishbooks.org</a> with any questions.'
                  } else if (result.subscriber_already_exists) {
                    return 'Recipient already has a subscription. Please email us at <a href="mailto:nureads@jewishbooks.org">nureads@jewishbooks.org</a> with any questions.'
                  }
                  return undefined
                },
              })}
            />
            {errors.email?.message && (
              <div
                className="form-item--error-message"
                dangerouslySetInnerHTML={{ __html: errors.email.message }}
              />
            )}
          </div>
        </div>
      </fieldset>
    </div>
  )
}

export default RecipientsFullNameAndEmailAddress
